import type { TableFiltersConfig } from "@shared/types/filters"
import { entityFilters, getEntityFilters } from "./entity-filters"

import { getPaymentBulkActions } from "../actions/payment-actions"
import { getMerchantBulkActions } from "../actions/merchant-actions"
import { getOrderBulkActions } from "../actions/order-actions"
import { getRefundBulkActions } from "../actions/refund-actions"
import { getDisputeBulkActions } from "../actions/dispute-actions"
import { getCustomerBulkActions } from "../actions/customer-actions"
import { getTerminalBulkActions } from "../actions/terminal-actions"
import { getProductBulkActions } from "../actions/product-actions"
import { getPaymentLinkActions } from "../actions/payment-link-actions"
import { getEcommerceBulkActions } from "../actions/ecommerce-actions"
import { getInventoryBulkActions } from "../actions/inventory-actions"
import { getInvoiceBulkActions } from "../actions/invoice-actions"
import { getBillingScheduleBulkActions } from "../actions/billing-schedule-actions"
import { getPlanBulkActions } from "../actions/plan-actions"
import { getSubscriptionBulkActions } from "../actions/subscription-actions"
import { getQuoteBulkActions } from "../actions/quote-actions"
import { getPricingTableBulkActions } from "../actions/pricing-table-actions"

import { getMerchantColumnConfig } from "../tables/columns/merchant-columns"
import { getPaymentColumnConfig } from "../tables/columns/payment-columns"
import { getCustomerColumnConfig } from "../tables/columns/customer-columns"
import { getDisputeColumnConfig } from "../tables/columns/dispute-columns"
import { getRefundColumnConfig } from "../tables/columns/refund-columns"
import { getOrderColumnConfig } from "../tables/columns/order-columns"
import { getTerminalColumnConfig } from "../tables/columns/terminal-columns"
import { getProductColumnConfig } from "../tables/columns/product-columns"
import { getPaymentLinkColumnConfig } from "../tables/columns/payment-link-columns"
import { getEcommerceColumnConfig } from "../tables/columns/ecommerce-columns"
import { getInventoryColumnConfig } from "../tables/columns/inventory-columns"
import { getInvoiceColumnConfig } from "../tables/columns/invoice-columns"
import { getBillingScheduleColumnConfig } from "../tables/columns/billing-schedule-columns.tsx"
import { getPlanColumnConfig } from "../tables/columns/plan-columns.tsx"
import { getSubscriptionColumnConfig } from "../tables/columns/subscription-columns"
import { getQuoteColumnConfig } from "../tables/columns/quote-columns"
import { getPricingTableColumnConfig } from "../tables/columns/pricing-table-columns"
import { getTariffColumnConfig } from "../tables/columns/tariff-columns"
import { getFeeColumnConfig } from "../tables/columns/fee-columns"
import { getFxScheduleColumnConfig } from "../tables/columns/fx-schedule-columns"
import { getBillingReserveColumnConfig } from "../tables/columns/billing-reserve-columns"
import { getBillingPayoutColumnConfig } from "../tables/columns/billing-payout-columns"
import { getMmDepositColumnConfig } from "../tables/columns/mm-deposit-columns"
import { getMmPayoutColumnConfig } from "../tables/columns/mm-payout-columns"
import { getNetworkTokenColumnConfig } from "../tables/columns/network-token-columns"
import { getNetworkTokenBulkActions } from "../actions/network-token-actions"
import { getUprdrBatchColumnConfig } from "../tables/columns/uprdr-batch-columns"
import { getUprdrBatchBulkActions } from "../actions/uprdr-batch-actions"
import { getNativeTokenColumnConfig } from "../tables/columns/native-token-columns"
import { getNativeTokenBulkActions } from "../actions/native-token-actions"
import { getEnrollmentColumnConfig } from "../tables/columns/enrollment-columns"
import { getEnrollmentBulkActions } from "../actions/enrollment-actions"
import { getCampaignColumnConfig } from "../tables/columns/campaign-columns"
import { getPosInventoryColumnConfig } from "../tables/columns/pos-inventory-columns"
import { getPosInventoryBulkActions } from "../actions/pos-inventory-actions"
import { getPosMenuColumnConfig } from "../tables/columns/pos-menu-columns"
import { getCardProgramColumnConfig } from "../tables/columns/card-program-columns"
import { getProgramProductColumnConfig } from "../tables/columns/program-product-columns"


const DEFAULT_CONFIG: Partial<TableFiltersConfig> = {
  visibleFilters: 3,
  enableSessionPersistence: true,
}

function createTableConfig(
  entityType: keyof typeof entityFilters,
  options: Partial<TableFiltersConfig> = {}
): TableFiltersConfig {
  const allFilters = getEntityFilters(entityType)

  return {
    title: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} Filters`,
    filters: [...allFilters],
    sessionKey: `${entityType}-filters`,
    entityType,
    ...DEFAULT_CONFIG,
    ...options,
  }
}

export const tableConfigs = {
  payments: createTableConfig("payments", {
    bulkActions: getPaymentBulkActions(),
    getColumnsForStatus: getPaymentColumnConfig,
  }),

  bankPayments: createTableConfig("bankPayments", {
    bulkActions: getPaymentBulkActions({}, 'bank'),
    getColumnsForStatus: (status) => getPaymentColumnConfig(status, 'bank'),
  }),

  orders: createTableConfig("orders", {
    bulkActions: getOrderBulkActions(),
    getColumnsForStatus: getOrderColumnConfig,
  }),

  refunds: createTableConfig("refunds", {
    bulkActions: getRefundBulkActions(),
    getColumnsForStatus: getRefundColumnConfig,
  }),

  disputes: createTableConfig("disputes", {
    bulkActions: getDisputeBulkActions(),
    getColumnsForStatus: getDisputeColumnConfig,
  }),

  customers: createTableConfig("customers", {
    bulkActions: getCustomerBulkActions(),
    getColumnsForStatus: getCustomerColumnConfig,
    visibleFilters: 4,
  }),

  terminals: createTableConfig("terminals", {
    bulkActions: getTerminalBulkActions(),
    getColumnsForStatus: getTerminalColumnConfig,
  }),

  merchants: createTableConfig("merchants", {
    bulkActions: getMerchantBulkActions(),
    getColumnsForStatus: getMerchantColumnConfig,
  }),

  products: createTableConfig("products", {
    bulkActions: getProductBulkActions(),
    getColumnsForStatus: getProductColumnConfig,
  }),

  ecommerce: createTableConfig("ecommerce", {
    bulkActions: getEcommerceBulkActions(),
    getColumnsForStatus: getEcommerceColumnConfig,
  }),

  "payment-link": createTableConfig("payment-link", {
    bulkActions: getPaymentLinkActions({}).bulkActions,
    getColumnsForStatus: getPaymentLinkColumnConfig,
  }),

  inventory: createTableConfig("inventory", {
    bulkActions: getInventoryBulkActions(),
    getColumnsForStatus: getInventoryColumnConfig,
  }),

  members: createTableConfig("members"),

  settlements: createTableConfig("settlements"),

  digital: createTableConfig("digital"),

  invoices: createTableConfig("invoices", {
    bulkActions: getInvoiceBulkActions(),
    getColumnsForStatus: getInvoiceColumnConfig,
  }),

  "billing-schedules": createTableConfig("billing-schedules", {
    bulkActions: getBillingScheduleBulkActions(),
    getColumnsForStatus: getBillingScheduleColumnConfig,
  }),

  "plan": createTableConfig("plan", {
    bulkActions: getPlanBulkActions(),
    getColumnsForStatus: getPlanColumnConfig,
  }),

  "subscriptions": createTableConfig("subscriptions", {
    bulkActions: getSubscriptionBulkActions(),
    getColumnsForStatus: getSubscriptionColumnConfig,
  }),

  "quotes": createTableConfig("quotes", {
    bulkActions: getQuoteBulkActions(),
    getColumnsForStatus: getQuoteColumnConfig,
  }),

  "pricing-table": createTableConfig("pricing-table", {
    bulkActions: getPricingTableBulkActions(),
    getColumnsForStatus: getPricingTableColumnConfig,
  }),

  "mm-payouts": createTableConfig("mm-payouts", {
    getColumnsForStatus: getMmPayoutColumnConfig,
  }),

  "mm-operations": createTableConfig("mm-operations"),
  "mm-adjustments": createTableConfig("mm-adjustments"),

  "mm-deposits": createTableConfig("mm-deposits", {
    getColumnsForStatus: getMmDepositColumnConfig,
  }),

  // Overview Rails
  'rail-alert': createTableConfig('rail-alert'),
  'review-item': createTableConfig('review-item'),

  // Card Rails
  authorization: createTableConfig("authorization"),
  clearing: createTableConfig("clearing"),
  'clearing-batch': createTableConfig("clearing-batch"),
  settlement: createTableConfig("settlement"),
  'card-settlement-transaction': createTableConfig('card-settlement-transaction'),
  "dispute-rail": createTableConfig("dispute-rail"),
  reconciliation: createTableConfig("reconciliation"),

  // Billing Admin
  tariff: createTableConfig("tariff", {
    getColumnsForStatus: getTariffColumnConfig,
  }),
  fee: createTableConfig("fee", {
    getColumnsForStatus: getFeeColumnConfig,
  }),
  "fx-schedule": createTableConfig("fx-schedule", {
    getColumnsForStatus: getFxScheduleColumnConfig,
  }),
  "billing-reserve": createTableConfig("billing-reserve", {
    getColumnsForStatus: getBillingReserveColumnConfig,
  }),
  "billing-payout": createTableConfig("billing-payout", {
    getColumnsForStatus: getBillingPayoutColumnConfig,
  }),

  "network-token": createTableConfig("network-token", {
    bulkActions: getNetworkTokenBulkActions(),
    getColumnsForStatus: getNetworkTokenColumnConfig,
  }),

  "uprdr-batch": createTableConfig("uprdr-batch", {
    bulkActions: getUprdrBatchBulkActions(),
    getColumnsForStatus: getUprdrBatchColumnConfig,
  }),

  "native-token": createTableConfig("native-token", {
    bulkActions: getNativeTokenBulkActions(),
    getColumnsForStatus: getNativeTokenColumnConfig,
  }),

  // Team & Security
  'team-member': createTableConfig('team-member'),
  'security-event': createTableConfig('security-event'),
  'security-policy': createTableConfig('security-policy'),

  // Sonar / Velocity
  velocity: createTableConfig('velocity'),
  'fraud-schedule': createTableConfig('fraud-schedule'),

  // Engagement
  enrollment: createTableConfig("enrollment", {
    bulkActions: getEnrollmentBulkActions(),
    getColumnsForStatus: getEnrollmentColumnConfig,
  }),

  campaign: createTableConfig("campaign", {
    getColumnsForStatus: getCampaignColumnConfig,
  }),

  // POS
  "pos-menu": createTableConfig("pos-menu", {
    getColumnsForStatus: getPosMenuColumnConfig,
  }),

  "pos-inventory": createTableConfig("pos-inventory", {
    bulkActions: getPosInventoryBulkActions(),
    getColumnsForStatus: getPosInventoryColumnConfig,
  }),

  // Programs (Bank-only)
  cardPrograms: createTableConfig("cardPrograms", {
    getColumnsForStatus: getCardProgramColumnConfig,
  }),

  programProducts: createTableConfig("programProducts", {
    getColumnsForStatus: getProgramProductColumnConfig,
  }),

} as const

export function getTableConfig(entityType: keyof typeof tableConfigs): TableFiltersConfig {
  return tableConfigs[entityType]
}

export function getEntityTypes(): (keyof typeof tableConfigs)[] {
  return Object.keys(tableConfigs) as (keyof typeof tableConfigs)[]
}

export type TableConfigKey = keyof typeof tableConfigs
